// --- React
import React, { useCallback } from 'react';
// --- UI Components
import Select from '@dtci/spellbook/Select';
import Typography from '@dtci/spellbook/Typography';
import Input from '@dtci/spellbook/Input';
import { InputLabel } from '../../InputLabel/InputLabel';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';

const productTypeOptions = [
    { label: 'Accounting Element', value: 'Accounting Element' },
    { label: 'Compilation Episode', value: 'Compilation Episode' },
    { label: 'Direct to Video', value: 'Direct to Video' },
    { label: 'Episode', value: 'Episode' },
    { label: 'Feature', value: 'Feature' },
    { label: 'Format', value: 'Format' },
    { label: 'Interactive', value: 'Interactive' },
    { label: 'Making Of', value: 'Making Of' },
    { label: 'Mini-Series', value: 'Mini-Series' },
    { label: 'Movie of the Week', value: 'Movie of the Week' },
    { label: 'New Media Film', value: 'New Media Film' },
    { label: 'Pilot', value: 'Pilot' },
    { label: 'Presentation', value: 'Presentation' },
    { label: 'Season', value: 'Season' },
    { label: 'Series', value: 'Series' },
    { label: 'Shorts', value: 'Shorts' },
    { label: 'Special', value: 'Special' },
    { label: 'Sports', value: 'Sports' },
];

const statusOptions = [
    { label: 'Accounting Element', value: 'Accounting Element' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Development', value: 'Development' },
    { label: 'Discretionary', value: 'Discretionary' },
    { label: 'Format', value: 'Format' },
    { label: 'Post-Production', value: 'Post-Production' },
    { label: 'Pre-Production', value: 'Pre-Production' },
    { label: 'Production', value: 'Production' },
    { label: 'Released', value: 'Released' },
    { label: 'Shutdown', value: 'Shutdown' },
    { label: 'Terminated', value: 'Terminated' },
    { label: 'Under Consideration', value: 'Under Consideration' },
];

/**
 * ClearanceMemoTitleForm Component
 *
 * This component renders a form for creating a Clearance Memo Title.
 * It includes fields for title, product type, and status.
 * It uses the Select component for product type and status selection.
 *
 * @component
 * @returns {JSX.Element} The rendered ClearanceMemoTitleForm component.
 */
export const ClearanceMemoTitleForm = () => {
    const form = useDialogsForm();

    // Handler to update the title field
    const handleChangeTitle = useCallback(
        (event) => {
            // Update the form state with the new title value
            form.setValue('title', event.target.value);
        },
        [form],
    );

    // Handler to update the product type field.
    const handleChangeProductType = useCallback(
        (selectedOption) => {
            // Update the form state with the selected product type value
            form.setValue('productType', selectedOption.value);
        },
        [form],
    );

    // Handler to update the status field.
    const handleChangeStatus = useCallback(
        (selectedOption) => {
            // Update the form state with the selected status value
            form.setValue('lifeCycleStatus', selectedOption.value);
        },
        [form],
    );

    return (
        <div className="flexBox">
            <Typography variant="T20">
                Note: Mapping to a product will require manual intervention
            </Typography>
            <InputLabel label="Title">
                <Input
                    labelText="title"
                    type="text"
                    onChange={handleChangeTitle}
                    value={form.state.formData.title || ''}
                    width="large"
                />
            </InputLabel>
            <InputLabel label="Product Type">
                <Select
                    name="type"
                    isSearchable
                    useMenuPortal
                    options={productTypeOptions}
                    value={productTypeOptions.find(
                        (item) => item.value === form.state.formData.productType,
                    )}
                    onChange={handleChangeProductType}
                />
            </InputLabel>
            <InputLabel label="Status">
                <Select
                    name="status"
                    isSearchable
                    useMenuPortal
                    options={statusOptions}
                    value={statusOptions.find(
                        (item) => item.value === form.state.formData.lifeCycleStatus,
                    )}
                    onChange={handleChangeStatus}
                />
            </InputLabel>
        </div>
    );
};

/**
 * useClearanceMemoTitlesDialog Hook
 *
 * This hook provides functionality to open the Clearance Memo Titles Dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openClearanceMemoTitlesDialog function.
 */
export const useClearanceMemoTitlesDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => () => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the Clearance Memo Titles Dialog
    const openClearanceMemoTitlesDialog = useCallback(
        // Function to open the dialog with the provided title and content
        (onConfirm) =>
            openDialog({
                title: 'Create a Clearance Memo Title',
                content: <ClearanceMemoTitleForm />,
                closeAction: true,
                primaryAction: {
                    label: 'Save',
                    action: handleSubmit(onConfirm),
                },
            }),
        [openDialog, handleSubmit],
    );

    return { openClearanceMemoTitlesDialog };
};
